/* =====================================================================
   T&S — Immersive Motion Layer
   Progressive enhancement on top of the GSAP/Lenis engine already loaded.
   Dependency-light + conflict-free: own rAF loop + IntersectionObserver.
   Modules: reveal · split-text · pointer-glass · 3D tilt · magnetic ·
            custom cursor · parallax · marquee · preloader.
   Respects prefers-reduced-motion.
   ===================================================================== */
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  var lerp = function (a, b, n) {
    return (1 - n) * a + n * b;
  };
  var clamp = function (v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
  };

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------------------------------------------------------------
     1. PRELOADER — reveal the page once loaded
  --------------------------------------------------------------- */
  function initPreloader() {
    var loader = document.getElementById('tns-loader');
    if (!loader) return;
    var done = function () {
      loader.classList.add('is-done');
      document.documentElement.classList.add('tns-ready');
      setTimeout(function () {
        if (loader.parentNode) loader.parentNode.removeChild(loader);
      }, 900);
    };
    if (document.readyState === 'complete') setTimeout(done, REDUCED ? 0 : 650);
    else
      window.addEventListener('load', function () {
        setTimeout(done, REDUCED ? 0 : 650);
      });
    // safety: never trap the user
    setTimeout(done, 4000);
  }

  /* ---------------------------------------------------------------
     2. SPLIT TEXT — [data-split="words|lines|chars"]
  --------------------------------------------------------------- */
  function splitText() {
    document.querySelectorAll('[data-split]').forEach(function (el) {
      if (el.dataset.splitDone) return;
      var mode = el.dataset.split;
      var text = el.textContent;
      el.setAttribute('aria-label', text);
      el.textContent = '';
      var frag = document.createDocumentFragment();

      if (mode === 'chars') {
        text.split('').forEach(function (ch, i) {
          var w = document.createElement('span');
          w.className = 'tns-word';
          w.setAttribute('aria-hidden', 'true');
          var inner = document.createElement('span');
          inner.textContent = ch === ' ' ? ' ' : ch;
          inner.style.setProperty('--w-delay', i * 18 + 'ms');
          w.appendChild(inner);
          frag.appendChild(w);
        });
      } else {
        /* words (default) */
        text
          .trim()
          .split(/\s+/)
          .forEach(function (word, i) {
            var w = document.createElement('span');
            w.className = 'tns-word';
            w.setAttribute('aria-hidden', 'true');
            var inner = document.createElement('span');
            inner.textContent = word;
            inner.style.setProperty('--w-delay', i * 55 + 'ms');
            w.appendChild(inner);
            frag.appendChild(w);
            frag.appendChild(document.createTextNode(' '));
          });
      }
      el.appendChild(frag);
      el.dataset.splitDone = '1';

      if (mode === 'lines') groupIntoLines(el);
    });
  }

  function groupIntoLines(el) {
    var words = [].slice.call(el.querySelectorAll('.tns-word'));
    if (!words.length) return;
    var lines = [],
      cur = [],
      top = null;
    words.forEach(function (w) {
      var t = w.offsetTop;
      if (top === null) top = t;
      if (Math.abs(t - top) > 4) {
        lines.push(cur);
        cur = [];
        top = t;
      }
      cur.push(w);
    });
    if (cur.length) lines.push(cur);
    el.textContent = '';
    lines.forEach(function (lineWords, li) {
      var line = document.createElement('span');
      line.className = 'tns-line';
      var inner = document.createElement('span');
      inner.style.setProperty('--line-delay', li * 90 + 'ms');
      lineWords.forEach(function (w, wi) {
        inner.appendChild(w);
        if (wi < lineWords.length - 1) inner.appendChild(document.createTextNode(' '));
      });
      line.appendChild(inner);
      el.appendChild(line);
    });
  }

  /* ---------------------------------------------------------------
     3. REVEAL — IntersectionObserver → .is-in
  --------------------------------------------------------------- */
  function initReveal() {
    var targets = document.querySelectorAll('[data-reveal], [data-split], .tns-line, .tns-stagger');
    if (REDUCED || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) {
        el.classList.add('is-in');
      });
      return;
    }
    // stagger children delays
    document.querySelectorAll('.tns-stagger').forEach(function (g) {
      [].slice.call(g.children).forEach(function (c, i) {
        c.style.setProperty('--reveal-delay', i * 80 + 'ms');
      });
    });
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ---------------------------------------------------------------
     4. POINTER-REACTIVE GLASS + GLARE  (--tns-mx/--tns-my)
  --------------------------------------------------------------- */
  function initPointerGlass() {
    if (REDUCED || !FINE) return;
    var els = document.querySelectorAll('.tns-glass, .tns-btn--glass, .tns-tilt, [data-glass]');
    els.forEach(function (el) {
      el.addEventListener('pointermove', function (ev) {
        var r = el.getBoundingClientRect();
        el.style.setProperty('--tns-mx', ((ev.clientX - r.left) / r.width) * 100 + '%');
        el.style.setProperty('--tns-my', ((ev.clientY - r.top) / r.height) * 100 + '%');
      });
    });
  }

  /* ---------------------------------------------------------------
     5. 3D TILT — .tns-tilt  (sets --rx/--ry)
  --------------------------------------------------------------- */
  function initTilt() {
    if (REDUCED || !FINE) return;
    document.querySelectorAll('.tns-tilt').forEach(function (el) {
      var max = parseFloat(el.dataset.tilt || '8');
      el.addEventListener('pointermove', function (ev) {
        var r = el.getBoundingClientRect();
        var px = (ev.clientX - r.left) / r.width - 0.5;
        var py = (ev.clientY - r.top) / r.height - 0.5;
        el.style.setProperty('--ry', (px * max).toFixed(2) + 'deg');
        el.style.setProperty('--rx', (-py * max).toFixed(2) + 'deg');
      });
      el.addEventListener('pointerleave', function () {
        el.style.setProperty('--rx', '0deg');
        el.style.setProperty('--ry', '0deg');
      });
    });
  }

  /* ---------------------------------------------------------------
     6. MAGNETIC — .tns-btn, [data-magnetic]
  --------------------------------------------------------------- */
  function initMagnetic() {
    if (REDUCED || !FINE) return;
    document.querySelectorAll('[data-magnetic], .tns-btn').forEach(function (el) {
      var strength = parseFloat(el.dataset.magnetic || '0.28');
      var raf,
        tx = 0,
        ty = 0,
        cx = 0,
        cy = 0;
      function loop() {
        cx = lerp(cx, tx, 0.18);
        cy = lerp(cy, ty, 0.18);
        el.style.transform = 'translate(' + cx.toFixed(2) + 'px,' + cy.toFixed(2) + 'px)';
        if (Math.abs(cx - tx) > 0.1 || Math.abs(cy - ty) > 0.1) raf = requestAnimationFrame(loop);
      }
      el.addEventListener('pointermove', function (ev) {
        var r = el.getBoundingClientRect();
        tx = (ev.clientX - (r.left + r.width / 2)) * strength;
        ty = (ev.clientY - (r.top + r.height / 2)) * strength;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(loop);
      });
      el.addEventListener('pointerleave', function () {
        tx = 0;
        ty = 0;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(loop);
      });
    });
  }

  /* ---------------------------------------------------------------
     7. CUSTOM CURSOR
  --------------------------------------------------------------- */
  function initCursor() {
    if (REDUCED || !FINE) return;
    var ring = document.createElement('div');
    ring.className = 'tns-cursor';
    var dot = document.createElement('div');
    dot.className = 'tns-cursor-dot';
    document.body.appendChild(ring);
    document.body.appendChild(dot);
    var mx = window.innerWidth / 2,
      my = window.innerHeight / 2,
      rx = mx,
      ry = my;
    window.addEventListener('pointermove', function (e) {
      mx = e.clientX;
      my = e.clientY;
      dot.style.transform = 'translate(' + mx + 'px,' + my + 'px) translate(-50%,-50%)';
    });
    (function loop() {
      rx = lerp(rx, mx, 0.18);
      ry = lerp(ry, my, 0.18);
      ring.style.transform = 'translate(' + rx + 'px,' + ry + 'px) translate(-50%,-50%)';
      requestAnimationFrame(loop);
    })();
    var hot = 'a, button, .tns-btn, .kala-btn, [data-magnetic], .tns-card, input, select, textarea, [role="button"]';
    document.addEventListener('pointerover', function (e) {
      if (e.target.closest && e.target.closest(hot)) ring.classList.add('is-hot');
    });
    document.addEventListener('pointerout', function (e) {
      if (e.target.closest && e.target.closest(hot)) ring.classList.remove('is-hot');
    });
  }

  /* ---------------------------------------------------------------
     8. PARALLAX — [data-parallax="0.2"]  (sets --py)
  --------------------------------------------------------------- */
  function initParallax() {
    if (REDUCED) return;
    var els = [].slice.call(document.querySelectorAll('[data-parallax]'));
    if (!els.length) return;
    var ticking = false;
    function update() {
      var vh = window.innerHeight;
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.bottom < -200 || r.top > vh + 200) return;
        var speed = parseFloat(el.dataset.parallax || '0.15');
        var center = r.top + r.height / 2 - vh / 2;
        el.style.setProperty('--py', (-center * speed).toFixed(1) + 'px');
      });
      ticking = false;
    }
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true },
    );
    window.addEventListener('resize', update);
    update();
  }

  /* ---------------------------------------------------------------
     9. MARQUEE — [data-marquee] duplicate track for seamless loop
  --------------------------------------------------------------- */
  function initMarquee() {
    document.querySelectorAll('[data-marquee] .tns-marquee__track').forEach(function (track) {
      if (track.dataset.cloned) return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.parentNode.appendChild(clone);
      track.dataset.cloned = '1';
    });
  }

  /* ---------------------------------------------------------------
     10. GOLDEN THREAD SPINE — [data-thread-spine]
     A running-stitch guide path spanning the page; a solid gold
     thread is drawn along it by scroll progress, needle at the tip.
  --------------------------------------------------------------- */
  function initThreadSpine() {
    var host = document.querySelector('[data-thread-spine]');
    if (!host) return;
    var svg = host.querySelector('.tns-spine__svg');
    var guide = host.querySelector('.tns-spine__guide');
    var thread = host.querySelector('.tns-spine__thread');
    var needle = host.querySelector('.tns-spine__needle');
    if (!svg || !guide || !thread || !needle) return;

    var length = 0;

    function build() {
      var main = document.getElementById('MainContent');
      if (!main) return;
      var W = document.documentElement.clientWidth;
      var top = window.innerHeight * 0.92; // start as the hero ends
      var bottom = main.offsetTop + main.offsetHeight - 80;
      var H = Math.max(bottom, top + 600);
      var narrow = W < 768;
      var inset = narrow ? 0.16 : 0.08; // weave amplitude
      var xL = W * inset;
      var xR = W * (1 - inset);
      var step = narrow ? 620 : 780; // vertical distance per weave

      host.style.height = H + 'px';
      svg.setAttribute('width', W);
      svg.setAttribute('height', H);
      svg.setAttribute('viewBox', '0 0 ' + W + ' ' + H);

      var d = 'M ' + (W / 2).toFixed(1) + ' ' + top.toFixed(1);
      var y = top;
      var side = 0; // 0 → right, 1 → left
      while (y < bottom - step) {
        var y2 = Math.min(y + step, bottom);
        var xT = side === 0 ? xR : xL;
        d +=
          ' C ' +
          (side === 0 ? xR : xL).toFixed(1) +
          ' ' +
          (y + step * 0.25).toFixed(1) +
          ', ' +
          xT.toFixed(1) +
          ' ' +
          (y2 - step * 0.25).toFixed(1) +
          ', ' +
          (W / 2).toFixed(1) +
          ' ' +
          y2.toFixed(1);
        y = y2;
        side = 1 - side;
      }
      guide.setAttribute('d', d);
      thread.setAttribute('d', d);
      length = thread.getTotalLength();
      thread.style.strokeDasharray = length + ' ' + length;
      draw();
    }

    function progress() {
      var doc = document.documentElement;
      var max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return 0;
      // draw between 4% and 96% of the scroll journey
      var p = (window.scrollY / max - 0.04) / 0.92;
      return clamp(p, 0, 1);
    }

    function draw() {
      if (!length) return;
      var p = progress();
      thread.style.strokeDashoffset = length * (1 - p);
      var at = thread.getPointAtLength(length * p);
      var ahead = thread.getPointAtLength(Math.min(length * p + 2, length));
      var angle = (Math.atan2(ahead.y - at.y, ahead.x - at.x) * 180) / Math.PI;
      needle.setAttribute(
        'transform',
        'translate(' + at.x.toFixed(1) + ',' + at.y.toFixed(1) + ') rotate(' + angle.toFixed(1) + ')',
      );
      needle.style.opacity = p > 0.001 && p < 0.999 ? 1 : 0;
    }

    if (REDUCED) {
      build();
      thread.style.display = 'none';
      needle.style.display = 'none';
      return;
    }

    var ticking = false;
    window.addEventListener(
      'scroll',
      function () {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(function () {
            draw();
            ticking = false;
          });
        }
      },
      { passive: true },
    );

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(build, 180);
    });

    // rebuild once everything (fonts/images) has settled the layout
    build();
    window.addEventListener('load', function () {
      setTimeout(build, 350);
    });
  }

  /* ---------------------------------------------------------------
     boot
  --------------------------------------------------------------- */
  initPreloader();
  ready(function () {
    splitText();
    initReveal();
    initPointerGlass();
    initTilt();
    initParallax();
    initMarquee();
    initThreadSpine();
    // cinema layer (tns-cinema.js) already provides a custom cursor +
    // magnetic buttons — installing a second copy makes elements flicker
    var cinemaActive = document.documentElement.hasAttribute('data-kala-cinema');
    if (!cinemaActive) {
      initMagnetic();
      if (FINE) initCursor();
    }
    document.documentElement.classList.add('tns-immersive-on');
  });

  // expose for section scripts
  window.TNS = window.TNS || {};
  window.TNS.refresh = function () {
    splitText();
    initReveal();
    initPointerGlass();
    initTilt();
    initMagnetic();
    initMarquee();
  };
})();
